import { useCallback, useEffect } from 'react';

export default function usePdfZoomControls({
  pageRefs,
  pageDimensions,
  setPositionUpdateTrigger,
  setManualZoom,
  zoomLevel,
  setZoomLevel,
  setZoomInputValue,
  setPageScale,
  baseScaleRef,
  setIsEditingZoom,
  zoomInputValue,
  scaleCalculatedRef,
  numPages,
  pageScale,
}) {
  const updatePageDimensions = useCallback(() => {
    Object.keys(pageRefs.current).forEach(pageNum => {
      const pageContainer = pageRefs.current[pageNum];
      if (pageContainer) {
        const pageElement = pageContainer.querySelector('.react-pdf__Page');
        if (pageElement) {
          const rect = pageElement.getBoundingClientRect();
          pageDimensions.current[pageNum] = {
            width: rect.width,
            height: rect.height
          };
        }
      }
    });

    setPositionUpdateTrigger(prev => prev + 1);
  }, [pageRefs, pageDimensions, setPositionUpdateTrigger]);

  const handleZoomIn = useCallback(() => {
    setManualZoom(true);
    const newZoomLevel = Math.min(zoomLevel + 25, 200);
    setZoomLevel(newZoomLevel);
    setZoomInputValue(newZoomLevel.toString());
    const newScale = baseScaleRef.current * (newZoomLevel / 100);
    setPageScale(newScale);
    setTimeout(() => {
      updatePageDimensions();
    }, 150);
  }, [setManualZoom, zoomLevel, setZoomLevel, setZoomInputValue, baseScaleRef, setPageScale, updatePageDimensions]);

  const handleZoomOut = useCallback(() => {
    setManualZoom(true);
    const newZoomLevel = Math.max(zoomLevel - 25, 50);
    setZoomLevel(newZoomLevel);
    setZoomInputValue(newZoomLevel.toString());
    const newScale = baseScaleRef.current * (newZoomLevel / 100);
    setPageScale(newScale);
    setTimeout(() => {
      updatePageDimensions();
    }, 150);
  }, [setManualZoom, zoomLevel, setZoomLevel, setZoomInputValue, baseScaleRef, setPageScale, updatePageDimensions]);

  const handleFitToWidth = useCallback(() => {
    setManualZoom(false);
    setZoomLevel(100);
    setZoomInputValue('100');
    if (baseScaleRef.current > 0) {
      setPageScale(baseScaleRef.current);
      setTimeout(() => {
        updatePageDimensions();
      }, 150);
    }
  }, [setManualZoom, setZoomLevel, setZoomInputValue, baseScaleRef, setPageScale, updatePageDimensions]);

  const applyCustomZoom = useCallback(() => {
    const numericValue = parseFloat(zoomInputValue);

    if (!isNaN(numericValue) && numericValue >= 50 && numericValue <= 200) {
      setManualZoom(true);
      setZoomLevel(numericValue);
      const newScale = baseScaleRef.current * (numericValue / 100);
      setPageScale(newScale);
      setIsEditingZoom(false);
      setTimeout(() => {
        updatePageDimensions();
      }, 150);
    } else {
      setZoomInputValue(zoomLevel.toString());
      setIsEditingZoom(false);
    }
  }, [zoomInputValue, setManualZoom, setZoomLevel, baseScaleRef, setPageScale, setIsEditingZoom, updatePageDimensions, setZoomInputValue, zoomLevel]);

  const handleZoomInputChange = useCallback((e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setZoomInputValue(value);
    }
  }, [setZoomInputValue]);

  const handleZoomInputBlur = useCallback(() => {
    applyCustomZoom();
  }, [applyCustomZoom]);

  const handleZoomInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyCustomZoom();
    } else if (e.key === 'Escape') {
      setZoomInputValue(zoomLevel.toString());
      setIsEditingZoom(false);
    }
  }, [applyCustomZoom, setZoomInputValue, zoomLevel, setIsEditingZoom]);

  useEffect(() => {
    if (scaleCalculatedRef.current && numPages) {
      const timeoutId = setTimeout(() => {
        updatePageDimensions();
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [scaleCalculatedRef, numPages, pageScale, updatePageDimensions]);

  const handleZoomDisplayClick = useCallback(() => {
    setIsEditingZoom(true);
    setZoomInputValue(zoomLevel.toString());
  }, [setIsEditingZoom, setZoomInputValue, zoomLevel]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleFitToWidth,
    handleZoomInputChange,
    handleZoomInputBlur,
    handleZoomInputKeyDown,
    handleZoomDisplayClick,
  };
}
